import { useState } from "react";
import { useAuth, api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, CheckCircle2 } from "lucide-react";

export default function PasswordStep({ onDone }) {
    const { refresh } = useAuth();
    const [form, setForm] = useState({ current: "", next: "", confirm: "" });
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const rules = [
        { ok: form.next.length >= 8, label: "Au moins 8 caractères" },
        { ok: /[A-Z]/.test(form.next), label: "Une majuscule" },
        { ok: /\d/.test(form.next), label: "Un chiffre" },
        { ok: /[^A-Za-z0-9]/.test(form.next), label: "Un caractère spécial" },
    ];
    const allOk = rules.every((r) => r.ok);
    const match = form.next.length > 0 && form.next === form.confirm;

    const submit = async (e) => {
        e.preventDefault();
        if (!allOk || !match) return;
        setSubmitting(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current,
                new_password: form.next,
            });
            await refresh();
            toast.success("Mot de passe mis à jour");
            onDone();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="mx-auto max-w-lg space-y-5" data-testid="onboarding-step-password">
            <div>
                <label className="block text-xs font-semibold uppercase tracking-wider">Mot de passe provisoire</label>
                <div className="relative mt-2">
                    <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />
                    <input
                        type={show ? "text" : "password"}
                        required
                        value={form.current}
                        onChange={(e) => setForm((f) => ({ ...f, current: e.target.value }))}
                        data-testid="onboarding-current-password"
                        className="w-full rounded-xl border border-brand-line py-3.5 pl-11 pr-12 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                    />
                    <button
                        type="button"
                        onClick={() => setShow((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-smoke hover:text-brand-ink"
                        aria-label="Afficher"
                    >
                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                </div>
            </div>
            <div>
                <label className="block text-xs font-semibold uppercase tracking-wider">Nouveau mot de passe</label>
                <input
                    type={show ? "text" : "password"}
                    required
                    value={form.next}
                    onChange={(e) => setForm((f) => ({ ...f, next: e.target.value }))}
                    data-testid="onboarding-new-password"
                    className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3.5 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                />
            </div>
            <div>
                <label className="block text-xs font-semibold uppercase tracking-wider">Confirmer</label>
                <input
                    type={show ? "text" : "password"}
                    required
                    value={form.confirm}
                    onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                    data-testid="onboarding-confirm-password"
                    className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3.5 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                />
                {!match && form.confirm.length > 0 && (
                    <div className="mt-2 text-xs text-red-600">Les mots de passe ne correspondent pas.</div>
                )}
            </div>

            <div className="rounded-2xl border border-brand-line bg-[#FAFAFA] p-4 text-sm">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-brand-ink">
                    Exigences de sécurité
                </div>
                <ul className="space-y-1.5">
                    {rules.map((r) => (
                        <li key={r.label} className={`flex items-center gap-2 ${r.ok ? "text-green-700" : "text-brand-smoke"}`}>
                            <CheckCircle2 className={`h-4 w-4 ${r.ok ? "" : "opacity-30"}`} />
                            {r.label}
                        </li>
                    ))}
                </ul>
            </div>

            <div className="pt-2">
                <button
                    type="submit"
                    disabled={!allOk || !match || submitting}
                    data-testid="onboarding-password-submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#d9772c] disabled:opacity-50"
                >
                    {submitting ? "Mise à jour…" : "Enregistrer et continuer"}
                </button>
            </div>
        </form>
    );
}
